import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Flag } from 'lucide-react'
import { Input } from '@/custolink-newdesign/ui/input'
import { Button } from '@/custolink-newdesign/ui/button'
import { Badge } from '@/custolink-newdesign/ui/badge'
import { AdmSupportVideoCallSessionApi } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'

/**
 * Mark and list key moments (D-07): a worker can flag a moment at any time, even while nothing
 * is recording. Each moment keeps its wall-clock time and shows whether it lines up with a
 * recorded segment or was captured with recording off - a moment is never dropped for that
 * reason (T-04-47: label text is rendered as plain React children, with no raw-HTML injection
 * anywhere in this file). Worker-only - only ever instantiated by RemoteSupportRoomPage's
 * sidePanel slot (T-04-44).
 */
export function KeyMomentsPanel(p: { orgId: number; callId: number }) {
  const [label, setLabel] = useState('')

  const moments = useQuery({
    queryKey: [
      'AdmSupportVideoCallSessionApi',
      AdmSupportVideoCallSessionApi.getMoments.name,
      p.orgId,
      p.callId,
    ],
    queryFn: () => AdmSupportVideoCallSessionApi.getMoments(p.orgId, p.callId),
    refetchOnWindowFocus: false,
  })

  const addMoment = useMutation({
    mutationFn: (text: string) =>
      AdmSupportVideoCallSessionApi.addMoment(p.orgId, p.callId, {
        label: text.trim() === '' ? 'Key moment' : text.trim(),
      }),
    onSuccess: () => {
      setLabel('')
      moments.refetch()
    },
  })

  const submit = () => {
    if (addMoment.isPending) return
    addMoment.mutate(label)
  }

  const items = [...(moments.data ?? [])].sort(
    (a, b) => b.occurredAtEpochMs - a.occurredAtEpochMs,
  )

  return (
    <div className="cl:flex cl:flex-col cl:gap-3">
      <h3 className="cl:text-sm cl:font-semibold">Key moments</h3>

      <div className="cl:flex cl:items-start cl:gap-2">
        <Input
          value={label}
          maxLength={200}
          placeholder="What just happened?"
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          className="cl:min-h-[44px] cl:shrink-0"
          disabled={addMoment.isPending}
          onClick={submit}
        >
          <Flag className="cl:size-4" />
          Mark moment
        </Button>
      </div>

      {addMoment.isError && (
        <p role="alert" className="cl:text-sm cl:text-destructive">
          Could not save the moment. Try again.
        </p>
      )}

      {items.length === 0 ? (
        <p className="cl:text-sm cl:text-muted-foreground">No key moments yet.</p>
      ) : (
        <ul className="cl:flex cl:flex-col cl:gap-2">
          {items.map((m) => (
            <li
              key={m.id}
              className="cl:flex cl:flex-col cl:gap-1 cl:rounded-md cl:border cl:border-border cl:p-2"
            >
              <div className="cl:flex cl:items-center cl:justify-between cl:gap-2">
                <span className="cl:text-sm cl:font-medium">{m.label}</span>
                {m.notRecorded ? (
                  <Badge
                    variant="outline"
                    title="Recording was off at this moment; the time is kept"
                  >
                    Not recorded
                  </Badge>
                ) : m.segmentIndex != null ? (
                  <Badge variant="secondary">Recorded - part {m.segmentIndex}</Badge>
                ) : (
                  <Badge variant="outline">Recording deleted</Badge>
                )}
              </div>
              <span className="cl:text-xs cl:text-muted-foreground">
                {new Date(m.occurredAtEpochMs).toLocaleTimeString()} - {m.authorDisplayName}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default KeyMomentsPanel
